import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Mail, Ticket as TicketIcon } from "lucide-react";
import { api } from "../../lib/api";
import { formatDateTime, isPastDate } from "../../lib/utils-format";

export default function BookingsPage() {
    const { t, i18n } = useTranslation();
    const [bookings, setBookings] = useState([]);
    const [tab, setTab] = useState("upcoming");
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);
    const locale = i18n.language === "fr" ? "fr-FR" : i18n.language === "ht" ? "fr-FR" : "en-US";

    const load = async () => {
        setLoading(true);
        setErrored(false);
        try {
            const { data } = await api.get("/api/my-bookings");
            setBookings(data || []);
        } catch (e) {
            setErrored(true);
            toast.error(t("common.error"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const upcoming = bookings.filter((b) => b.event?.start_datetime && !isPastDate(b.event.start_datetime));
    const past = bookings.filter((b) => b.event?.start_datetime && isPastDate(b.event.start_datetime));

    const resendTickets = async (bookingId) => {
        try {
            await api.post(`/api/bookings/${bookingId}/send-tickets`);
            toast.success(t("bookings.tickets_sent"));
        } catch {
            toast.error(t("common.error"));
        }
    };

    const list = tab === "upcoming" ? upcoming : past;

    if (loading) return <div className="p-10 text-muted-foreground">{t("common.loading")}</div>;

    if (errored) return (
        <div data-testid="bookings-page-error" className="p-10 max-w-md mx-auto text-center space-y-4">
            <h2 className="font-heading font-black text-2xl">{t("common.error")}</h2>
            <p className="text-muted-foreground">{t("bookings.load_failed_hint")}</p>
            <button onClick={load} data-testid="bookings-retry" className="btn-primary">
                {t("common.retry")}
            </button>
        </div>
    );

    return (
        <div data-testid="bookings-page" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
            <h1 className="font-heading font-black text-3xl md:text-4xl">{t("bookings.title")}</h1>
            <div className="flex gap-2 p-1 rounded-full bg-muted inline-flex">
                <button
                    data-testid="tab-upcoming"
                    onClick={() => setTab("upcoming")}
                    className={`px-6 py-2 rounded-full text-sm font-semibold transition-all ${tab === "upcoming" ? "bg-white shadow-soft" : "text-muted-foreground"}`}
                >
                    {t("bookings.upcoming")}
                </button>
                <button
                    data-testid="tab-past"
                    onClick={() => setTab("past")}
                    className={`px-6 py-2 rounded-full text-sm font-semibold transition-all ${tab === "past" ? "bg-white shadow-soft" : "text-muted-foreground"}`}
                >
                    {t("bookings.past")}
                </button>
            </div>

            {list.length === 0 ? (
                <div className="rounded-2xl border-2 border-dashed border-border p-12 text-center text-muted-foreground">
                    {tab === "upcoming" ? t("bookings.no_upcoming") : t("bookings.no_past")}
                </div>
            ) : (
                <div className="space-y-4">
                    {list.map((b) => (
                        <div
                            key={b.id}
                            data-testid={`booking-${b.id}`}
                            className="bento-card overflow-hidden"
                        >
                            <div className="flex flex-col md:flex-row md:items-stretch">
                                {b.event?.featured_image_url && (
                                    <div className="md:w-48 md:self-stretch aspect-square md:aspect-auto flex-shrink-0 bg-muted">
                                        <img src={b.event.featured_image_url} alt="" className="w-full h-full object-cover" />
                                    </div>
                                )}
                                <div className="flex-1 p-5 space-y-3">
                                    <h3 className="font-heading font-bold text-xl">{b.event?.title}</h3>
                                    <div className="space-y-1 text-sm text-muted-foreground">
                                        <div className="flex items-center gap-2"><Calendar className="h-4 w-4" /> {formatDateTime(b.event?.start_datetime, locale)}</div>
                                        <div className="flex items-center gap-2"><MapPin className="h-4 w-4" /> {b.event?.venue?.name}</div>
                                    </div>
                                    <div className="flex flex-wrap gap-2 pt-1">
                                        {(b.tickets || []).map((tk) => (
                                            <span key={tk.ticket_id} className="badge-pill bg-muted">
                                                <TicketIcon className="h-3 w-3 mr-1" />
                                                {tk.ticket_type_name}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="flex flex-wrap gap-2 pt-2">
                                        <button
                                            data-testid={`send-email-${b.id}`}
                                            onClick={() => resendTickets(b.id)}
                                            className="btn-outline !py-2 !px-4 text-sm inline-flex items-center gap-2"
                                        >
                                            <Mail className="h-4 w-4" />
                                            {t("bookings.send_tickets_email")}
                                        </button>
                                        <Link
                                            to={`/events/${b.event_id}`}
                                            className="btn-outline !py-2 !px-4 text-sm"
                                        >
                                            {t("bookings.view_event")}
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
